import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LOGO_URL, companyInfo } from '../../data/mock';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram } from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: t('footer.websites'), path: '/webseiten' },
      { name: t('footer.webApps'), path: '/web-apps' },
      { name: t('footer.ecommerce'), path: '/e-commerce' },
      { name: t('footer.pwa'), path: '/pwa' },
    ],
    company: [
      { name: t('footer.aboutUs'), path: '/ueber-uns' },
      { name: t('footer.portfolio'), path: '/portfolio' },
      { name: t('footer.pricing'), path: '/preise' },
      { name: t('footer.contact'), path: '/kontakt' },
    ],
    legal: [
      { name: t('footer.imprint'), path: '/impressum' },
      { name: t('footer.privacy'), path: '/datenschutz' },
      { name: t('footer.terms'), path: '/agb' },
    ],
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src={LOGO_URL}
                alt="Beta-Webagentur Logo"
                className="h-12 w-auto object-contain"
              />
              <span className="font-semibold text-xl">{companyInfo.name}</span>
            </Link>
            <p className="text-slate-400 mb-6 max-w-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a
                href={companyInfo.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors duration-200"
              >
                <Linkedin size={18} />
              </a>
              <a
                href={companyInfo.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors duration-200"
              >
                <Twitter size={18} />
              </a>
              <a
                href={companyInfo.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-600 transition-colors duration-200"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-6">{t('footer.servicesTitle')}</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-lg mb-6">{t('footer.companyTitle')}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-6">{t('footer.contactTitle')}</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <Mail size={18} className="text-blue-500" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <Phone size={18} className="text-blue-500" />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin size={18} className="text-blue-500 mt-0.5" />
                  <span className="text-sm">{companyInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} {companyInfo.name}. {t('footer.rights')}
          </p>
          <div className="flex gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-slate-500 text-sm hover:text-white transition-colors duration-200"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
